import config, { getDocumentUrl } from "@/config";
import { uuid } from "@/utils/transform";

import { Api } from "./Api";
import {
  AdvertisementConsent,
  ApiResponse,
  ImageData,
  Interaction,
  InteractionResult,
  InteractionType,
  NewContactInput,
  Participant,
  ParticipantAutofill,
  ParticipationSaveResponse,
  ParticipationSubmit,
  PollResult,
  QuizVote,
  QuizVoteResponse,
  Slide,
  VotingResult,
} from "./Model";

export interface InteractionApi {
  getInteraction(
    interactionId: string,
    isPreview: boolean
  ): Promise<ApiResponse<Interaction>>;

  getInteractionResult(
    interactionId: string,
    interactionType: string
  ): Promise<ApiResponse<InteractionResult>>;

  getInteractionAndResults(
    interactionId: string,
    isPreview: boolean
  ): Promise<[ApiResponse<Interaction>, ApiResponse<InteractionResult>]>;

  hansIdExists(hansId: string): Promise<boolean>;

  postPollResult(
    interactionId: string,
    slideId: string,
    optionId: string,
    isFirstVote: boolean
  ): Promise<ApiResponse<void>>;

  postPollResults(
    interactionId: string,
    options: PollResult[],
    isFirstVote: boolean
  ): Promise<ApiResponse<void>>;

  postVotingResults(
    interactionId: string,
    options: VotingResult[],
    isFirstVote: boolean
  ): Promise<ApiResponse<void>>;

  postOpinionResults(
    opinionId: string,
    value: number[],
    referrer?: string
  ): Promise<ApiResponse<void>>;

  postOpinionFeedback(
    opinionId: string,
    answer: number,
    message: string
  ): Promise<ApiResponse<void>>;

  postQuizResults(
    quizId: string,
    slideId: string,
    optionId: string,
    isFirstVote: boolean,
    referrer?: string
  ): Promise<ApiResponse<void>>;

  postParticipation(participation: Participant): Promise<ApiResponse<void>>;

  getParticipantAutofill(
    token: string,
    interactionID: string
  ): Promise<ApiResponse<ParticipantAutofill>>;

  postParticipationV2(
    participation: ParticipationSubmit,
    token: string
  ): Promise<ApiResponse<ParticipationSaveResponse>>;

  putParticipationContact(
    interactionID: string,
    contact: NewContactInput,
    token: string
  ): Promise<ApiResponse<void>>;

  deleteParticipantAutofillContact(
    contactId: string,
    token: string
  ): Promise<ApiResponse<void>>;

  getQuizCorrectAnswersCount(
    quizId: string,
    results: QuizVote[]
  ): Promise<ApiResponse<QuizVoteResponse>>;
}

class InteractionService implements InteractionApi {
  private api: Api;
  private typeMap: any = {
    poll: "interactions",
    opinion: "opinions",
    quiz: "quizzes",
    voting: "votings",
  };

  constructor(host = "") {
    this.api = new Api(host);
  }

  public getInteraction(
    interactionId: string,
    isPreview = false
  ): Promise<ApiResponse<Interaction>> {
    const timestamp: Date = new Date();
    return isPreview
      ? this.api.get<Interaction>(
          `/la-interactive-api/${
            config.apiVersion
          }/interactions/public/${interactionId}?ts=${timestamp.getTime()}`
        )
      : this.api.get<Interaction>(
          `/la-interactive-api/${config.apiVersion}/interactions/public/${interactionId}`
        );
  }

  /**
   * This function is a deep search for all images in the interaction object.
   *
   * Hint: The whole interaction config json is already loaded on clint-side,
   * but the function could also request an endpoint instead.
   * @param interaction
   */
  public static getImagesData(interaction: Interaction): ImageData[] {
    const isImage = (value: string): boolean => {
      return /\.(gif|jpe?g|png)/i.test(value);
    };

    /**
     * Helper function
     * Search for image urls and additional copyright and/or source
     * @param obj
     */
    const findImages = (obj: any): ImageData[] => {
      let images: ImageData[] = [];
      Object.keys(obj).forEach((key: string) => {
        const value = obj[key];
        const copyright = `${key}Copyright`;
        const source = `${key}Source`;
        if (typeof value === "object" && value !== null) {
          // Attention: Recursion
          images = [...images, ...findImages(value)];
        } else if (isImage(value) && (obj[copyright] || obj[source])) {
          images.push({
            url: value,
            copyright: obj[copyright],
            source: obj[source],
          });
        }
      });
      return images;
    };

    // remove duplicates by url
    return findImages(interaction).filter(
      (a: ImageData, index: number, self: ImageData[]) =>
        self.findIndex((b: ImageData) => a.url === b.url) === index
    );
  }

  public getInteractionResult(
    interactionId: string,
    interactionType: string
  ): Promise<ApiResponse<InteractionResult>> {
    const type = this.typeMap[interactionType];
    return this.api.get<InteractionResult>(
      `/la-interactive-api/${type}/${interactionId}/results`
    );
  }

  public async getInteractionAndResults(
    interactionId: string,
    isPreview = false
  ): Promise<[ApiResponse<Interaction>, ApiResponse<InteractionResult>]> {
    const interaction = await this.getInteraction(interactionId, isPreview);
    if (interaction.ok) {
      const interactionResult = await this.getInteractionResult(
        interactionId,
        interaction.data!.type
      );
      return [interaction, interactionResult];
    } else {
      throw new Error(`${interaction.status} - ${interaction.text}`);
    }
  }

  public async hansIdExists(
    hansId: string,
    referrer: string = getDocumentUrl()
  ): Promise<boolean> {
    const response: ApiResponse<AdvertisementConsent> =
      await this.api.get<AdvertisementConsent>(
        `/la-interactive-api/participants/advertisement-consent/${hansId}`,
        undefined,
        { "X-Referrer": referrer }
      );

    return Promise.resolve(!!response.data?.hansId);
  }

  public postPollResult(
    interactionId: string,
    slideId: string,
    optionId: string,
    isFirstVote: boolean,
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/interactions/${interactionId}/results`,
      { interactionId, slideId, optionId, value: 1, isFirstVote },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public postPollResults(
    interactionId: string,
    options: PollResult[],
    isFirstVote: boolean,
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/interactions/${interactionId}/results`,
      { interactionId, options, isFirstVote },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public postVotingResults(
    interactionId: string,
    options: VotingResult[],
    isFirstVote: boolean,
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/votings/${interactionId}/results`,
      { interactionId, options, isFirstVote },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public postOpinionResults(
    opinionId: string,
    value: number[],
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/opinions/${opinionId}/results`,
      { opinionId, value },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public postQuizResults(
    quizId: string,
    slideId: string,
    optionId: string,
    isFirstVote: boolean,
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/quizzes/${quizId}/results`,
      { quizId, slideId, optionId, value: 1, isFirstVote },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public postParticipation(
    participation: Participant,
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/participants`,
      participation,
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public getParticipantAutofill(
    token: string,
    interactionID: string
  ): Promise<ApiResponse<ParticipantAutofill>> {
    return this.api
      .get<ParticipantAutofill>(
        `/la-interactive-api/v2/participants/autofill`,
        { interactionID },
        { Authorization: `Bearer ${token}` }
      )
      .then((response) => {
        // Contacts is a JSON array by contract, but an empty list was once serialised as null;
        // reading `.length`/`.map` on that threw and dropped every registration to the legacy form.
        // Normalise here, at the single point every caller (initial + refresh paths) flows through.
        if (response.data && response.data.contacts == null) {
          response.data.contacts = [];
        }
        return response;
      });
  }

  public postParticipationV2(
    participation: ParticipationSubmit,
    token: string
  ): Promise<ApiResponse<ParticipationSaveResponse>> {
    return this.api.post<ParticipationSaveResponse>(
      `/la-interactive-api/v2/participants`,
      participation,
      { Authorization: `Bearer ${token}`, "Idempotency-Key": uuid() }
    );
  }

  public putParticipationContact(
    interactionID: string,
    contact: NewContactInput,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.api.put<void>(
      `/la-interactive-api/v2/participants/${encodeURIComponent(
        interactionID
      )}/contact`,
      contact,
      { Authorization: `Bearer ${token}` }
    );
  }

  public deleteParticipantAutofillContact(
    contactId: string,
    token: string
  ): Promise<ApiResponse<void>> {
    return this.api.remove(
      `/la-interactive-api/v2/participants/autofill/${encodeURIComponent(
        contactId
      )}`,
      { Authorization: `Bearer ${token}` }
    );
  }

  public postOpinionFeedback(
    opinionId: string,
    answer: number,
    message: string,
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<void>> {
    return this.api.post<void>(
      `/la-interactive-api/opinions/${opinionId}/feedback`,
      { opinionId, answer, message },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public getQuizCorrectAnswersCount(
    quizId: string,
    results: QuizVote[],
    referrer: string = getDocumentUrl()
  ): Promise<ApiResponse<QuizVoteResponse>> {
    return this.api.post<QuizVoteResponse>(
      `/la-interactive-api/v2/interactions/${quizId}/checkResults`,
      {
        results,
      },
      { Authorization: this.api.signRequest(), "X-Referrer": referrer }
    );
  }

  public static hasBackgroundImage(interaction: Interaction): boolean {
    if (interaction.type === InteractionType.Opinion) {
      return false;
    }

    return (
      !!interaction.background ||
      !!interaction.slides.find((slide: Slide) => !!slide.background)
    );
  }
}

export default InteractionService;
